import React, { useState, useEffect } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';

// Components
import classNames from 'classnames';
import Dialog from '@material-ui/core/Dialog';
import { SlideTitle } from '@sift/skipper/components/SlideDialog';

interface Props {
  dialogClassName?: string;
  slideContainerClassName?: string;
  open: boolean;
  setDialogOpen: (open: boolean) => void;
  title?: string;
  subtitle?: string;
  titleActions?: (slideIndex: number, setSlideIndex: SetSlideIndexType) => React.ReactNode;
  defaultSlideIndex?: number;
  children: (slideIndex: number, setSlideIndex: SetSlideIndexType) => React.ReactNode[];
  dialogProps?: any;
}

type SetSlideIndexType = (slideIndex: number) => void;

export default function SlideDialog({
  open,
  setDialogOpen,
  title,
  subtitle,
  titleActions,
  dialogProps,
  defaultSlideIndex = 0,
  dialogClassName,
  slideContainerClassName,
  children,
}: Props) {
  const classes = useStyles();
  const onClose = () => setDialogOpen(false);
  const [currentSlideIndex, setSlideIndex] = useState(defaultSlideIndex);

  useEffect(() => {
    setSlideIndex(defaultSlideIndex);
  }, [defaultSlideIndex]);

  const displaySlide = () => {
    const content = children(currentSlideIndex, setSlideIndex).map(
      (slide: React.ReactNode, slideIndex: number) => {
        const isLeft = slideIndex < currentSlideIndex;
        const showSlideClass = isLeft ? classes.leftSlide : classes.rightSlide;
        const hideSlideClass = isLeft ? classes.leftSlideHidden : classes.rightSlideHidden;

        return (
          <div
            className={classNames(classes.slide, showSlideClass, {
              [hideSlideClass]: slideIndex !== currentSlideIndex,
            })}
            key={slideIndex}
          >
            {slide}
          </div>
        );
      },
    );

    return content;
  };

  const TitleActionsElem = titleActions && titleActions(currentSlideIndex, setSlideIndex);

  return (
    <Dialog className={dialogClassName} open={open} onClose={onClose} fullWidth {...dialogProps}>
      {title && <SlideTitle title={title} subtitle={subtitle} titleActions={TitleActionsElem} />}
      <div
        className={classNames(classes.slideContainer, slideContainerClassName)}
        data-testid="slide-container"
      >
        {displaySlide()}
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: any) => {
  return createStyles({
    slideContainer: {
      flex: '1 1 auto',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 400,
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      },
    },
    slide: {
      position: 'absolute',
      width: '100%',
      minWidth: '100%',
      height: '100%',
      transition: theme.transitions.create(['left', 'opacity'], {
        duration: theme.transitions.duration.complex,
      }),
      display: 'flex',
      flexDirection: 'column',
    },
    leftSlide: {
      left: 0,
      opacity: 1,
    },
    leftSlideHidden: {
      left: '-100%',
      opacity: 0,
    },
    rightSlide: {
      left: 0,
      opacity: 1,
    },
    rightSlideHidden: {
      left: '100%',
      opacity: 0,
    },
  });
});
